import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import type { ProjectManifest } from "@pix/shared";
import {
  CHARA_POSITIONS,
  type CharaPosition,
  type SceneRow,
  type VariableOp,
} from "../grid/gridTypes";
import { parseToGrid } from "../grid/parseToGrid";
import {
  deleteLines,
  findChoiceBlockRange,
  findSpeechBlockRange,
  insertLineBefore,
  moveBlock,
  replaceChoiceBlock,
  replaceLine,
  replaceSpeechBlock,
  type ChoiceOption,
} from "../grid/applyEdit";

interface VarEditTarget {
  row: SceneRow;
  op: VariableOp;
}

/** Inspector など外部から SceneGrid の編集モーダルを開くための命令ハンドル */
export interface SceneGridHandle {
  editMessage(row: SceneRow): void;
  editChoices(row: SceneRow): void;
  editBgm(row: SceneRow): void;
  editBg(row: SceneRow): void;
  editSe(row: SceneRow): void;
  editChara(row: SceneRow, pos: CharaPosition): void;
  editJump(row: SceneRow): void;
  editLabel(row: SceneRow): void;
}
import { BUILTIN_SE_BY_PATH } from "@pix/builtin-se";
import { cacheListByProject, type AssetRecord } from "../storage/assetStore";
import { MessageEditor } from "./MessageEditor";
import { PickerModal, type PickerOption } from "./PickerModal";
import { SePickerModal, type SePickResult } from "./SePickerModal";
import { CharaPicker } from "./CharaPicker";
import { ContextMenu, type ContextMenuItem } from "./ContextMenu";
import { ChoiceEditor } from "./ChoiceEditor";
import { TextInputModal } from "./TextInputModal";

type TextEdit =
  | { kind: "jump"; row: SceneRow; current: string }
  | { kind: "call"; row: SceneRow; current: string }
  | { kind: "label"; row: SceneRow; current: string }
  | { kind: "set-insert"; row: SceneRow }
  | { kind: "set-edit"; row: SceneRow; op: VariableOp };

interface Props {
  /** .pix ソース */
  source: string;
  /** ファイル名(エラー表示用) */
  filename: string;
  /** manifest(キャラ名・パス解決) */
  manifest: ProjectManifest;
  /** アセットの projectId(プレビュー画像取得) */
  projectId: string;
  /** 組み込み SE のプレビュー再生に使う engine の URL */
  engineUrl: string;
  /** 行クリック時のコールバック(ソース行番号) */
  onRowClick?: (sourceLine: number) => void;
  /** 行選択時のコールバック(行オブジェクト) */
  onRowSelect?: (row: SceneRow) => void;
  /** 選択中の行 ID(ハイライト) */
  activeRowId?: string;
  /** セル編集でソースが変わった時のコールバック */
  onSourceChange?: (newSource: string) => void;
  /** アセット D&D で manifest 側のエントリを自動追加する時のコールバック */
  onManifestChange?: (next: ProjectManifest) => void;
  /** 指定行へスクロール・ハイライト(LINT / OUTLINE クリック等) */
  scrollToLine?: number | null;
  /** scrollToLine 適用後にコールバック(親側で null にリセットしてもらう) */
  onScrolled?: () => void;
}

export const SceneGrid = forwardRef<SceneGridHandle, Props>(function SceneGrid(
  {
    source,
    filename,
    manifest,
    projectId,
    engineUrl,
    onRowClick,
    onRowSelect,
    activeRowId,
    onSourceChange,
    onManifestChange,
    scrollToLine,
    onScrolled,
  }: Props,
  ref,
) {
  const grid = useMemo(() => parseToGrid(source, filename), [source, filename]);
  const [highlightId, setHighlightId] = useState<string | null>(null);

  /** 行 ID → <tr> 要素のマップ(scrollIntoView 用) */
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  /** scrollToLine 指示があれば、該当行を見つけてスクロール+ハイライト */
  useEffect(() => {
    if (scrollToLine == null) return;
    const row = grid.rows.find(
      (r) =>
        r.sourceRange.start <= scrollToLine && r.sourceRange.end >= scrollToLine,
    );
    if (!row) {
      onScrolled?.();
      return;
    }
    const el = rowRefs.current.get(row.id);
    if (el) {
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      setHighlightId(row.id);
      window.setTimeout(() => setHighlightId(null), 1600);
    }
    onScrolled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToLine]);
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [objectUrls, setObjectUrls] = useState<Map<string, string>>(new Map());
  const [editingMessage, setEditingMessage] = useState<SceneRow | null>(null);
  const [editingCell, setEditingCell] = useState<
    { kind: "bgm" | "bg"; row: SceneRow } | null
  >(null);
  const [editingSe, setEditingSe] = useState<SceneRow | null>(null);
  const [editingChara, setEditingChara] = useState<
    { row: SceneRow; position: CharaPosition } | null
  >(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; row: SceneRow } | null>(
    null,
  );
  const [editingChoice, setEditingChoice] = useState<SceneRow | null>(null);
  const [editingText, setEditingText] = useState<TextEdit | null>(null);
  const [dragging, setDragging] = useState<{ rowId: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    rowId: string;
    position: "above" | "below";
  } | null>(null);

  useImperativeHandle(ref, () => ({
    editMessage: (row) => setEditingMessage(row),
    editChoices: (row) => setEditingChoice(row),
    editBgm: (row) => setEditingCell({ kind: "bgm", row }),
    editBg: (row) => setEditingCell({ kind: "bg", row }),
    editSe: (row) => setEditingSe(row),
    editChara: (row, position) => setEditingChara({ row, position }),
    editJump: (row) => {
      const m = row.markers.find((x) => x.kind === "jump" || x.kind === "call");
      if (!m) return;
      const kind: "jump" | "call" = m.kind === "call" ? "call" : "jump";
      const current = (m as { target: string }).target ?? "";
      setEditingText({ kind, row, current });
    },
    editLabel: (row) => {
      const m = row.markers.find((x) => x.kind === "label");
      if (!m) return;
      setEditingText({
        kind: "label",
        row,
        current: (m as { name: string }).name,
      });
    },
  }));

  const labelSuggestions = useMemo(() => {
    const labels: string[] = [];
    for (const row of grid.rows) {
      for (const m of row.markers) {
        if (m.kind === "label") labels.push(m.name);
      }
    }
    return labels;
  }, [grid]);

  const onChoiceClick = (row: SceneRow) => {
    if (!onSourceChange) return;
    if (!row.choices) return;
    setEditingChoice(row);
  };

  const onChoiceSave = (choices: ChoiceOption[]) => {
    if (!editingChoice || !onSourceChange) return;
    const newSource = replaceChoiceBlock(source, editingChoice.sourceLine, choices);
    onSourceChange(newSource);
    setEditingChoice(null);
  };

  const onTextSave = (value: string) => {
    if (!editingText || !onSourceChange) return;
    let newSource: string | null = null;
    switch (editingText.kind) {
      case "jump":
        newSource = replaceLine(source, editingText.row.sourceLine, `@jump ${value}`);
        break;
      case "call":
        newSource = replaceLine(source, editingText.row.sourceLine, `@call ${value}`);
        break;
      case "label": {
        const name = value.startsWith("##") ? value.replace(/^#+\s*/, "") : value;
        newSource = replaceLine(source, editingText.row.sourceLine, `## ${name}`);
        break;
      }
      case "set-insert":
        newSource = insertLineBefore(source, editingText.row.sourceLine, `@set ${value}`);
        break;
      case "set-edit": {
        const prefix = editingText.op.kind === "unset" ? "@unset" : "@set";
        newSource = replaceLine(source, editingText.op.line, `${prefix} ${value}`);
        break;
      }
    }
    if (newSource !== null) onSourceChange(newSource);
    setEditingText(null);
  };

  const onVarOpClick = (target: VarEditTarget) => {
    if (!onSourceChange) return;
    setEditingText({ kind: "set-edit", row: target.row, op: target.op });
  };

  const onVarOpDelete = (target: VarEditTarget) => {
    if (!onSourceChange) return;
    if (!confirm(`@${target.op.kind} ${target.op.raw} を削除します。よろしいですか?`)) return;
    onSourceChange(deleteLines(source, target.op.line, target.op.line));
  };

  const onJumpEdit = (row: SceneRow) => {
    const m = row.markers.find((x) => x.kind === "jump" || x.kind === "call");
    if (!m) return;
    const kind: "jump" | "call" = m.kind === "call" ? "call" : "jump";
    const current = (m as { target: string }).target ?? "";
    setEditingText({ kind, row, current });
  };

  const onLabelEdit = (row: SceneRow) => {
    const m = row.markers.find((x) => x.kind === "label");
    if (!m) return;
    setEditingText({ kind: "label", row, current: (m as { name: string }).name });
  };

  const onSetInsert = (row: SceneRow) => {
    setEditingText({ kind: "set-insert", row });
  };

  const onMessageClick = (row: SceneRow) => {
    if (!onSourceChange) return;
    if (row.message === null) return;
    setEditingMessage(row);
  };

  const onMessageSave = (speaker: string | null, text: string) => {
    if (!editingMessage || !onSourceChange) return;
    const newSource = replaceSpeechBlock(source, editingMessage.sourceLine, speaker, text);
    onSourceChange(newSource);
    setEditingMessage(null);
  };

  const onCellClick = (kind: "bgm" | "bg", row: SceneRow) => {
    if (!onSourceChange) return;
    setEditingCell({ kind, row });
  };

  const onSeCellClick = (row: SceneRow) => {
    if (!onSourceChange) return;
    setEditingSe(row);
  };

  const onPickerSelect = (value: string) => {
    if (!editingCell || !onSourceChange) return;
    const row = editingCell.row;
    let line: string;
    switch (editingCell.kind) {
      case "bgm":
        line = value === "" ? "@bgm stop" : `@bgm play file=${value}`;
        break;
      case "bg":
        line = value === "" ? "@bg hide" : `@bg show name=${value}`;
        break;
      default:
        return;
    }
    if (line) {
      const newSource = insertLineBefore(source, row.sourceLine, line);
      onSourceChange(newSource);
    }
    setEditingCell(null);
  };

  const onSeSelect = (result: SePickResult) => {
    if (!editingSe || !onSourceChange) return;
    const row = editingSe;
    const parts: string[] = [];
    if (result.kind === "stop") {
      parts.push("@se stop");
      parts.push(`slot=${result.slot}`);
    } else {
      if (!result.selection) {
        setEditingSe(null);
        return;
      }
      // 組み込み SE は manifest.audio.se に未登録なら自動追加する
      let fileId: string;
      if (result.selection.source === "builtin") {
        fileId = ensureBuiltinSeEntry(result.selection.builtin.id, result.selection.builtin.path);
      } else {
        fileId = result.selection.file;
      }
      parts.push("@se play");
      parts.push(`slot=${result.slot}`);
      parts.push(`file=${fileId}`);
      if (result.vol !== undefined) parts.push(`vol=${result.vol}`);
      if (result.loop) parts.push(`loop=1`);
    }
    const line = parts.join(" ");
    // 既存の @se 行があればその行を置換、無ければ行の手前に挿入
    const existingLine = row.se?.sourceLine;
    const newSource =
      existingLine !== undefined
        ? replaceLine(source, existingLine, line)
        : insertLineBefore(source, row.sourceLine, line);
    onSourceChange(newSource);
    setEditingSe(null);
  };

  /** 組み込み SE の builtin path に対応する manifest エントリを返す。無ければ追加。 */
  function ensureBuiltinSeEntry(builtinId: string, builtinPath: string): string {
    const map = manifest.audio?.se ?? {};
    // 既に同じ path のエントリがあれば再利用
    const existing = Object.entries(map).find(([, def]) => def.file === builtinPath);
    if (existing) return existing[0];
    // 組み込み id は "ui-audio/click1" / "pix-studio/00_小物/.../置く" など。
    // "/" や記号は "_" に置換、ただし Unicode の文字・数字(日本語含む)は保持する。
    const safeBase = builtinId
      .replace(/\//g, "_")
      .replace(/[^\p{L}\p{N}_]/gu, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
    let id = safeBase;
    let n = 1;
    while (Object.prototype.hasOwnProperty.call(map, id)) {
      id = `${safeBase}_${++n}`;
    }
    onManifestChange?.({
      ...manifest,
      audio: {
        bgm: manifest.audio?.bgm ?? {},
        ...(manifest.audio?.voice ? { voice: manifest.audio.voice } : {}),
        se: { ...map, [id]: { file: builtinPath } },
      },
    });
    return id;
  }

  const onCharaCellClick = (row: SceneRow, position: CharaPosition) => {
    if (!onSourceChange) return;
    setEditingChara({ row, position });
  };

  /** AssetCard ドロップ: bgm / bg / se セルへ受け入れて、必要なら manifest にエントリ追加 */
  const onCellDrop = (
    e: React.DragEvent,
    cellKind: "bgm" | "bg" | "se",
    row: SceneRow,
  ) => {
    if (!onSourceChange) return;
    const raw = e.dataTransfer.getData("application/x-pix-asset");
    if (!raw) return;
    e.preventDefault();
    let data: { path: string; mime: string; category: string };
    try {
      data = JSON.parse(raw);
    } catch {
      return;
    }
    // 該当 manifest エントリを探す or 作る
    const id = ensureManifestEntry(cellKind, data.path);
    if (!id) return;
    let line: string;
    if (cellKind === "bgm") line = `@bgm play file=${id}`;
    else if (cellKind === "bg") line = `@bg show name=${id}`;
    else line = `@se play slot=0 file=${id}`;
    // SE は既存の @se 行があればそれを置換、他のセルは従来通り行の前に挿入
    if (cellKind === "se" && row.se?.sourceLine !== undefined) {
      onSourceChange(replaceLine(source, row.se.sourceLine, line));
    } else {
      onSourceChange(insertLineBefore(source, row.sourceLine, line));
    }
  };

  /** path に対応する manifest エントリの ID を返す。無ければ自動生成して onManifestChange を呼ぶ。 */
  function ensureManifestEntry(
    kind: "bgm" | "bg" | "se",
    path: string,
  ): string | null {
    if (kind === "bg") {
      const existing = Object.entries(manifest.backgrounds ?? {}).find(
        ([, def]) => def.file === path,
      );
      if (existing) return existing[0];
      const id = deriveAssetId(path, manifest.backgrounds ?? {});
      onManifestChange?.({
        ...manifest,
        backgrounds: {
          ...(manifest.backgrounds ?? {}),
          [id]: { file: path },
        },
      });
      return id;
    }
    const ns = kind === "bgm" ? "bgm" : "se";
    const map = manifest.audio?.[ns] ?? {};
    const existing = Object.entries(map).find(([, def]) => def.file === path);
    if (existing) return existing[0];
    const id = deriveAssetId(path, map);
    onManifestChange?.({
      ...manifest,
      audio: {
        bgm: manifest.audio?.bgm ?? {},
        se: manifest.audio?.se ?? {},
        ...(manifest.audio?.voice ? { voice: manifest.audio.voice } : {}),
        [ns]: { ...map, [id]: { file: path } },
      },
    });
    return id;
  }

  const onCharaPickerSelect = (id: string | null, expression: string) => {
    if (!editingChara || !onSourceChange) return;
    const { row, position } = editingChara;
    const current = row.chara[position];

    const lines: string[] = [];
    if (id === null) {
      // クリア: 既存があれば hide
      if (current) lines.push(`@chara hide id=${current.id}`);
    } else if (current?.id === id) {
      // 同じキャラ、表情だけ変える
      if (current.expression !== expression) {
        lines.push(`@chara face id=${id} expression=${expression}`);
      }
    } else {
      // 別キャラに差し替え: 既存を hide してから新規 show
      if (current) lines.push(`@chara hide id=${current.id}`);
      lines.push(`@chara show id=${id} pos=${position} face=${expression}`);
    }

    if (lines.length > 0) {
      const combined = lines.join("\n");
      const newSource = insertLineBefore(source, row.sourceLine, combined);
      onSourceChange(newSource);
    }
    setEditingChara(null);
  };

  const getRowSourceRange = (row: SceneRow): { start: number; end: number } => {
    if (row.message !== null) {
      return findSpeechBlockRange(source, row.sourceLine);
    }
    if (row.choices) {
      return findChoiceBlockRange(source, row.sourceLine);
    }
    return { start: row.sourceLine, end: row.sourceLine };
  };

  const onRowDelete = (row: SceneRow) => {
    if (!onSourceChange) return;
    const range = getRowSourceRange(row);
    if (!confirm(`${range.end - range.start + 1} 行を削除します。よろしいですか?`)) return;
    onSourceChange(deleteLines(source, range.start, range.end));
  };

  const onRowInsertAbove = (row: SceneRow) => {
    if (!onSourceChange) return;
    onSourceChange(insertLineBefore(source, row.sourceLine, "> 新規メッセージ\n"));
  };

  const onRowInsertBelow = (row: SceneRow) => {
    if (!onSourceChange) return;
    const range = getRowSourceRange(row);
    onSourceChange(insertLineBefore(source, range.end + 1, "\n> 新規メッセージ"));
  };

  const buildContextMenuItems = (row: SceneRow): ContextMenuItem[] => {
    const items: ContextMenuItem[] = [];
    if (row.message !== null) {
      items.push({
        label: "メッセージを編集...",
        onClick: () => onMessageClick(row),
        separator: true,
      });
    }
    if (row.choices) {
      items.push({
        label: "選択肢を編集...",
        onClick: () => onChoiceClick(row),
        separator: true,
      });
    }
    const hasJump = row.markers.some((m) => m.kind === "jump");
    const hasCall = row.markers.some((m) => m.kind === "call");
    const hasLabel = row.markers.some((m) => m.kind === "label");
    if (hasJump) {
      items.push({
        label: "ジャンプ先を編集...",
        onClick: () => onJumpEdit(row),
      });
    }
    if (hasCall) {
      items.push({
        label: "コール先を編集...",
        onClick: () => onJumpEdit(row),
      });
    }
    if (hasLabel) {
      items.push({
        label: "ラベル名を編集...",
        onClick: () => onLabelEdit(row),
      });
    }
    if (hasJump || hasCall || hasLabel) {
      items[items.length - 1]!.separator = true;
    }
    items.push({
      label: "効果音を追加...",
      onClick: () => onSeCellClick(row),
    });
    items.push({
      label: "@set 変数操作を追加...",
      onClick: () => onSetInsert(row),
      separator: true,
    });
    items.push({
      label: "↑ 上に空行を追加",
      onClick: () => onRowInsertAbove(row),
    });
    items.push({
      label: "↓ 下に空行を追加",
      onClick: () => onRowInsertBelow(row),
      separator: true,
    });
    items.push({
      label: "✕ この行を削除",
      onClick: () => onRowDelete(row),
      danger: true,
    });
    return items;
  };

  const buildPickerProps = (): {
    title: string;
    options: PickerOption[];
    currentValue: string | null;
    emptyMessage: string;
  } | null => {
    if (!editingCell) return null;
    const row = editingCell.row;
    switch (editingCell.kind) {
      case "bgm": {
        const ids = Object.keys(manifest.audio?.bgm ?? {});
        const options: PickerOption[] = ids.map((id) => ({ value: id, label: id }));
        options.push({
          value: "",
          label: "(停止)",
          description: "@bgm stop",
          separator: true,
        });
        return {
          title: "BGM を選択",
          options,
          currentValue: row.bgm?.file ?? null,
          emptyMessage: "manifest の audio.bgm に BGM が登録されていません。",
        };
      }
      case "bg": {
        const ids = Object.keys(manifest.backgrounds ?? {});
        const options: PickerOption[] = ids.map((id) => ({ value: id, label: id }));
        options.push({
          value: "",
          label: "(非表示)",
          description: "@bg hide",
          separator: true,
        });
        return {
          title: "背景を選択",
          options,
          currentValue: row.bg?.name ?? null,
          emptyMessage: "manifest の backgrounds に背景が登録されていません。",
        };
      }
    }
  };
  const pickerProps = buildPickerProps();

  // アセットを読み込み、画像パス → object URL のマップを作る
  useEffect(() => {
    let canceled = false;
    cacheListByProject(projectId).then((list) => {
      if (canceled) return;
      setAssets(list);
      const m = new Map<string, string>();
      for (const a of list) {
        if (a.mime.startsWith("image/")) {
          m.set(a.path, URL.createObjectURL(a.blob));
        }
      }
      setObjectUrls((prev) => {
        for (const url of prev.values()) URL.revokeObjectURL(url);
        return m;
      });
    });
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, assets.length]);

  useEffect(() => {
    return () => {
      for (const url of objectUrls.values()) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (grid.error) {
    return (
      <div className="grid-error">
        <strong>パースエラー:</strong>
        <pre>{grid.error}</pre>
      </div>
    );
  }

  if (grid.rows.length === 0) {
    return <div className="grid-empty">(まだ行がありません)</div>;
  }

  return (
    <>
      <div className="scene-grid-wrap">
        <table className="scene-grid">
          <thead>
            <tr>
              <th className="col-num">#</th>
              <th className="col-state">状態</th>
              <th className="col-bgm">BGM</th>
              <th className="col-se">SE</th>
              <th className="col-bg">背景</th>
              {CHARA_POSITIONS.map((p) => (
                <th key={p} className="col-chara">
                  {posLabel(p)}
                </th>
              ))}
              <th className="col-msg">メッセージ</th>
              <th className="col-label">ラベル</th>
            </tr>
          </thead>
          <tbody>
            {grid.rows.map((row) => (
              <Row
                key={row.id}
                row={row}
                manifest={manifest}
                objectUrls={objectUrls}
                active={activeRowId === row.id}
                editable={!!onSourceChange}
                highlight={highlightId === row.id}
                trRef={(el) => {
                  if (el) rowRefs.current.set(row.id, el);
                  else rowRefs.current.delete(row.id);
                }}
                isDragging={dragging?.rowId === row.id}
                dropPosition={
                  dropTarget?.rowId === row.id ? dropTarget.position : null
                }
                onClick={() => {
                  onRowClick?.(row.sourceLine);
                  onRowSelect?.(row);
                }}
                onContextMenu={(e) => {
                  if (!onSourceChange) return;
                  e.preventDefault();
                  setContextMenu({ x: e.clientX, y: e.clientY, row });
                }}
                onMessageClick={() => onMessageClick(row)}
                onBgmClick={() => onCellClick("bgm", row)}
                onBgClick={() => onCellClick("bg", row)}
                onSeClick={() => onSeCellClick(row)}
                onCellDrop={(e, kind) => onCellDrop(e, kind, row)}
                onCharaClick={(p) => onCharaCellClick(row, p)}
                onChoiceClick={() => onChoiceClick(row)}
                onVarOpClick={(op) => onVarOpClick({ row, op })}
                onVarOpDelete={(op) => onVarOpDelete({ row, op })}
                onDragStartRow={() => setDragging({ rowId: row.id })}
                onDragOverRow={(e, position) => {
                  if (!dragging || dragging.rowId === row.id) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDropTarget({ rowId: row.id, position });
                }}
                onDropRow={() => {
                  if (!dragging || dragging.rowId === row.id || !onSourceChange) {
                    setDragging(null);
                    setDropTarget(null);
                    return;
                  }
                  const src = grid.rows.find((r) => r.id === dragging.rowId);
                  const tgt = row;
                  setDragging(null);
                  setDropTarget(null);
                  if (!src) return;
                  const insertBefore =
                    dropTarget?.position === "below"
                      ? tgt.sourceRange.end + 1
                      : tgt.sourceRange.start;
                  const newSource = moveBlock(
                    source,
                    src.sourceRange.start,
                    src.sourceRange.end,
                    insertBefore,
                  );
                  if (newSource !== source) onSourceChange(newSource);
                }}
                onDragEndRow={() => {
                  setDragging(null);
                  setDropTarget(null);
                }}
              />
            ))}
          </tbody>
        </table>
      </div>
      {editingMessage && editingMessage.message && (
        <MessageEditor
          initialSpeaker={editingMessage.message.speaker}
          initialText={editingMessage.message.plainText}
          onSave={onMessageSave}
          onCancel={() => setEditingMessage(null)}
        />
      )}
      {pickerProps && (
        <PickerModal
          title={pickerProps.title}
          options={pickerProps.options}
          currentValue={pickerProps.currentValue}
          emptyMessage={pickerProps.emptyMessage}
          onSelect={onPickerSelect}
          onCancel={() => setEditingCell(null)}
        />
      )}
      {editingSe && (
        <SePickerModal
          manifest={manifest}
          projectId={projectId}
          engineUrl={engineUrl}
          initial={
            editingSe.se
              ? {
                  kind: editingSe.se.kind,
                  ...(editingSe.se.file !== undefined ? { file: editingSe.se.file } : {}),
                  ...(editingSe.se.slot !== undefined ? { slot: editingSe.se.slot } : {}),
                  ...(editingSe.se.vol !== undefined ? { vol: editingSe.se.vol } : {}),
                  ...(editingSe.se.loop !== undefined ? { loop: editingSe.se.loop } : {}),
                }
              : null
          }
          onSelect={onSeSelect}
          onCancel={() => setEditingSe(null)}
        />
      )}
      {editingChara && (
        <CharaPicker
          manifest={manifest}
          position={editingChara.position}
          currentId={editingChara.row.chara[editingChara.position]?.id ?? null}
          currentExpression={editingChara.row.chara[editingChara.position]?.expression ?? null}
          onSelect={onCharaPickerSelect}
          onCancel={() => setEditingChara(null)}
        />
      )}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={buildContextMenuItems(contextMenu.row)}
          onClose={() => setContextMenu(null)}
        />
      )}
      {editingChoice && editingChoice.choices && (
        <ChoiceEditor
          initialChoices={editingChoice.choices.map((c) => ({
            text: c.text,
            target: c.target,
            condition: c.conditionRaw,
          }))}
          labelSuggestions={labelSuggestions}
          onSave={onChoiceSave}
          onCancel={() => setEditingChoice(null)}
        />
      )}
      {editingText && (
        <TextInputModal
          {...textEditModalProps(editingText, labelSuggestions)}
          onSave={onTextSave}
          onCancel={() => setEditingText(null)}
        />
      )}
    </>
  );
});

function textEditModalProps(
  edit: TextEdit,
  labelSuggestions: string[],
): {
  title: string;
  label: string;
  initialValue: string;
  placeholder?: string;
  suggestions?: string[];
} {
  const labelOptions = labelSuggestions.map((l) => `#${l}`);
  switch (edit.kind) {
    case "jump":
      return {
        title: "ジャンプ先を編集",
        label: "ジャンプ先 (#label / path#label)",
        initialValue: edit.current,
        placeholder: "#next_scene",
        suggestions: labelOptions,
      };
    case "call":
      return {
        title: "コール先を編集",
        label: "コール先 (#label / path#label)",
        initialValue: edit.current,
        placeholder: "#sub_routine",
        suggestions: labelOptions,
      };
    case "label":
      return {
        title: "ラベル名を編集",
        label: "ラベル名(## を除く識別子)",
        initialValue: edit.current,
        placeholder: "scene_start",
      };
    case "set-insert":
      return {
        title: "@set 変数操作を追加",
        label: "代入式(例: affection += 1)",
        initialValue: "",
        placeholder: "affection += 1",
      };
    case "set-edit":
      return {
        title: edit.op.kind === "unset" ? "@unset を編集" : "@set を編集",
        label:
          edit.op.kind === "unset"
            ? "解除する変数名"
            : "代入式(例: affection += 1)",
        initialValue: edit.op.raw,
        placeholder: edit.op.kind === "unset" ? "affection" : "affection += 1",
      };
  }
}

function Row({
  row,
  manifest,
  objectUrls,
  active,
  editable,
  highlight,
  trRef,
  isDragging,
  dropPosition,
  onClick,
  onContextMenu,
  onMessageClick,
  onBgmClick,
  onBgClick,
  onSeClick,
  onCharaClick,
  onChoiceClick,
  onVarOpClick,
  onVarOpDelete,
  onDragStartRow,
  onDragOverRow,
  onDropRow,
  onDragEndRow,
  onCellDrop,
}: {
  row: SceneRow;
  manifest: ProjectManifest;
  objectUrls: Map<string, string>;
  active: boolean;
  editable: boolean;
  highlight: boolean;
  trRef: (el: HTMLTableRowElement | null) => void;
  isDragging: boolean;
  dropPosition: "above" | "below" | null;
  onClick: () => void;
  onContextMenu: (e: ReactMouseEvent) => void;
  onMessageClick: () => void;
  onBgmClick: () => void;
  onBgClick: () => void;
  onSeClick: () => void;
  onCharaClick: (p: CharaPosition) => void;
  onChoiceClick: () => void;
  onVarOpClick: (op: VariableOp) => void;
  onVarOpDelete: (op: VariableOp) => void;
  onDragStartRow: () => void;
  onDragOverRow: (e: React.DragEvent, position: "above" | "below") => void;
  onDropRow: () => void;
  onDragEndRow: () => void;
  onCellDrop: (e: React.DragEvent, kind: "bgm" | "bg" | "se") => void;
}) {
  const isMarkerOnly = row.message === null && row.markers.length > 0;

  return (
    <tr
      ref={trRef}
      className={
        "scene-row" +
        (active ? " active" : "") +
        (isMarkerOnly ? " marker-row" : "") +
        (isDragging ? " dragging" : "") +
        (highlight ? " highlight" : "") +
        (dropPosition ? ` drop-${dropPosition}` : "")
      }
      onClick={onClick}
      onContextMenu={onContextMenu}
      onDragOver={(e) => {
        if (!editable) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const pos = e.clientY - rect.top < rect.height / 2 ? "above" : "below";
        onDragOverRow(e, pos);
      }}
      onDrop={(e) => {
        if (!editable) return;
        e.preventDefault();
        onDropRow();
      }}
      onDragEnd={onDragEndRow}
    >
      <td
        className="col-num"
        draggable={editable}
        onDragStart={(e) => {
          if (!editable) return;
          e.dataTransfer.effectAllowed = "move";
          e.dataTransfer.setData("text/plain", row.id);
          onDragStartRow();
        }}
        title={editable ? "ドラッグして並び替え" : ""}
        style={{ cursor: editable ? "grab" : "default" }}
      >
        {row.index}
      </td>

      <td className="col-state">{renderStateMarker(row)}</td>

      <td
        className={
          "col-bgm" + (row.changed.bgm ? "" : " carry") + (editable ? " clickable" : "")
        }
        onClick={(e) => {
          if (editable) {
            e.stopPropagation();
            onBgmClick();
          }
        }}
        onDragOver={(e) => {
          if (!editable) return;
          if (e.dataTransfer.types.includes("application/x-pix-asset")) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDrop={(e) => {
          if (!editable) return;
          onCellDrop(e, "bgm");
        }}
        title={editable ? "クリックで BGM を選択 / アセットをドロップ可" : ""}
      >
        {row.bgm ? <span>{row.bgm.file}</span> : <span className="empty">—</span>}
      </td>

      <td
        className={"col-se" + (editable ? " clickable" : "")}
        onClick={(e) => {
          if (editable) {
            e.stopPropagation();
            onSeClick();
          }
        }}
        onDragOver={(e) => {
          if (!editable) return;
          if (e.dataTransfer.types.includes("application/x-pix-asset")) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDrop={(e) => {
          if (!editable) return;
          onCellDrop(e, "se");
        }}
        title={editable ? "クリックで SE を編集 / アセットをドロップ可" : ""}
      >
        {row.se ? renderSeCell(row.se, manifest) : <span className="empty">—</span>}
      </td>

      <td
        className={
          "col-bg" + (row.changed.bg ? "" : " carry") + (editable ? " clickable" : "")
        }
        onClick={(e) => {
          if (editable) {
            e.stopPropagation();
            onBgClick();
          }
        }}
        onDragOver={(e) => {
          if (!editable) return;
          if (e.dataTransfer.types.includes("application/x-pix-asset")) {
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
          }
        }}
        onDrop={(e) => {
          if (!editable) return;
          onCellDrop(e, "bg");
        }}
        title={editable ? "クリックで背景を選択 / アセットをドロップ可" : ""}
      >
        {row.bg ? <BgThumb name={row.bg.name} manifest={manifest} objectUrls={objectUrls} /> : null}
      </td>

      {CHARA_POSITIONS.map((p) => (
        <td
          key={p}
          className={
            "col-chara" +
            (row.changed.chara[p] ? "" : " carry") +
            (editable ? " clickable" : "")
          }
          onClick={(e) => {
            if (editable) {
              e.stopPropagation();
              onCharaClick(p);
            }
          }}
          title={editable ? "クリックで立ち絵を選択" : ""}
        >
          {row.chara[p] ? (
            <CharaThumb
              id={row.chara[p]!.id}
              expression={row.chara[p]!.expression}
              manifest={manifest}
              objectUrls={objectUrls}
            />
          ) : null}
        </td>
      ))}

      <td className="col-msg">
        {row.message ? (
          <div
            className={"msg-cell" + (editable ? " editable" : "")}
            onClick={(e) => {
              if (editable) {
                e.stopPropagation();
                onMessageClick();
              }
            }}
            title={editable ? "クリックで編集" : ""}
          >
            {row.message.speaker && (
              <div
                className="msg-speaker"
                style={{ color: charaColor(row.message.speaker, manifest) }}
              >
                {row.message.speaker}
              </div>
            )}
            <div className="msg-text">{row.message.plainText}</div>
          </div>
        ) : row.choices ? (
          <div
            className={"choices-cell" + (editable ? " editable" : "")}
            onClick={(e) => {
              if (editable) {
                e.stopPropagation();
                onChoiceClick();
              }
            }}
            title={editable ? "クリックで選択肢を編集" : ""}
          >
            {row.choices.map((c, i) => (
              <div key={i} className="choice-line">
                <span className="choice-bullet">└</span>
                <span className="choice-text">{c.text}</span>
                <span className="choice-arrow">→</span>
                <span className="choice-target">{c.target}</span>
                {c.conditionRaw && <span className="choice-cond">@if {c.conditionRaw}</span>}
              </div>
            ))}
          </div>
        ) : null}
      </td>

      <td className="col-label">
        <div className="label-cell">
          {row.markers.map((m, i) => (
            <div key={i} className="label-marker">
              {markerToString(m)}
            </div>
          ))}
          {row.variableOps.map((op, i) => (
            <div
              key={`v${i}`}
              className={"label-varop" + (editable ? " clickable" : "")}
              onClick={(e) => {
                if (!editable) return;
                e.stopPropagation();
                onVarOpClick(op);
              }}
              onContextMenu={(e) => {
                if (!editable) return;
                e.preventDefault();
                e.stopPropagation();
                onVarOpDelete(op);
              }}
              title={editable ? "クリックで編集 / 右クリックで削除" : ""}
            >
              @{op.kind} {op.raw}
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

function renderSeCell(
  se: NonNullable<SceneRow["se"]>,
  manifest: ProjectManifest,
): React.ReactNode {
  const slot = se.slot ?? 0;
  if (se.kind === "stop") {
    return (
      <div className="se-cell se-cell-stop" title="効果音停止">
        <span className="se-stop-icon">■</span>
        <span className="se-slot">slot {slot}</span>
      </div>
    );
  }
  const badges: string[] = [];
  if (slot !== 0) badges.push(`slot ${slot}`);
  if (se.vol !== undefined && se.vol !== 100) badges.push(`vol ${se.vol}%`);
  if (se.loop) badges.push("loop");

  // 表示名解決: manifest.audio.se[se.file].file が組み込みパスなら BUILTIN_SE の name を採用、
  // それ以外(ユーザーアップロード)は manifest の ID をそのまま使う(=ユーザー命名)
  const def = se.file ? manifest.audio?.se?.[se.file] : undefined;
  const builtin = def ? BUILTIN_SE_BY_PATH[def.file] : undefined;
  const displayName = builtin?.name ?? se.file ?? "?";

  return (
    <div className="se-cell se-cell-play" title={se.file ? `file=${se.file}` : "効果音再生"}>
      <span className="se-flash">{displayName}</span>
      {badges.length > 0 && <span className="se-badges">{badges.join(" · ")}</span>}
    </div>
  );
}

function renderStateMarker(row: SceneRow): React.ReactNode {
  const marker = row.markers.find((m) => m.kind !== "macro" && m.kind !== "label");
  if (!marker) return null;
  switch (marker.kind) {
    case "jump":
      return <span className="state-tag tag-jump">↪</span>;
    case "call":
      return <span className="state-tag tag-call">▸</span>;
    case "return":
      return <span className="state-tag tag-return">◂</span>;
    case "end":
      return <span className="state-tag tag-end">■</span>;
    case "choice":
      return <span className="state-tag tag-choice">?</span>;
    case "wait":
      return <span className="state-tag tag-wait">⏱</span>;
    case "transition":
      return <span className="state-tag tag-transition">⇄</span>;
    case "effect":
      return <span className="state-tag tag-effect">✨</span>;
    case "screen":
      return <span className="state-tag tag-screen">⌬</span>;
    default:
      return null;
  }
}

function markerToString(m: SceneRow["markers"][number]): string {
  switch (m.kind) {
    case "label":
      return `//ラベル: ${m.name}`;
    case "jump":
      return `//Jump: ${m.target}`;
    case "call":
      return `//Call: ${m.target}`;
    case "return":
      return `//Return`;
    case "end":
      return `//End`;
    case "choice":
      return `//Choice`;
    case "macro":
      return `//Macro: ${m.name}`;
    case "wait":
      return `//Wait${m.ms !== undefined ? ` ${m.ms}ms` : ""}`;
    case "transition":
      return `//Transition: ${m.effect}${m.ms !== undefined ? ` ${m.ms}ms` : ""}`;
    case "effect":
      return `//Effect: ${m.name}`;
    case "screen":
      return `//Screen: ${m.effect}`;
  }
}

function posLabel(p: CharaPosition): string {
  switch (p) {
    case "left-edge":
      return "左端";
    case "left":
      return "左";
    case "center":
      return "中";
    case "right":
      return "右";
    case "right-edge":
      return "右端";
  }
}

function charaColor(speakerName: string, manifest: ProjectManifest): string {
  for (const [, def] of Object.entries(manifest.characters ?? {})) {
    if (def.name === speakerName && def.defaultColor) {
      return def.defaultColor;
    }
  }
  return "#ffd28a";
}

function BgThumb({
  name,
  manifest,
  objectUrls,
}: {
  name: string;
  manifest: ProjectManifest;
  objectUrls: Map<string, string>;
}) {
  const bg = manifest.backgrounds?.[name];
  const url = bg ? objectUrls.get(bg.file) : undefined;
  if (!url) {
    return <span className="thumb-label">{name}</span>;
  }
  return (
    <div className="thumb thumb-bg" title={name}>
      <img src={url} alt={name} />
    </div>
  );
}

function deriveAssetId(path: string, existing: Record<string, unknown>): string {
  const base = path
    .split("/")
    .pop()!
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-zA-Z0-9_]/g, "_") || "asset";
  let id = base;
  let n = 1;
  while (Object.prototype.hasOwnProperty.call(existing, id)) {
    id = `${base}_${++n}`;
  }
  return id;
}

function CharaThumb({
  id,
  expression,
  manifest,
  objectUrls,
}: {
  id: string;
  expression: string;
  manifest: ProjectManifest;
  objectUrls: Map<string, string>;
}) {
  const c = manifest.characters?.[id];
  const pattern = c?.facePattern ?? c?.spritePattern;
  const path = pattern
    ? pattern.replaceAll("{expression}", expression).replaceAll("{id}", id)
    : null;
  const url = path ? objectUrls.get(path) : undefined;
  const fallback = `${c?.name ?? id}/${expression}`;
  if (!url) {
    return <span className="thumb-label">{fallback}</span>;
  }
  return (
    <div className="thumb thumb-chara" title={fallback}>
      <img src={url} alt={fallback} />
      <div className="thumb-overlay">{expression}</div>
    </div>
  );
}
